import { doc, getDoc, increment, setDoc } from 'firebase/firestore';
import { countersRef } from '@api/Orders/ShortId';
import { Order } from '@api/Orders/Order';
import { ProductCategory } from '@api/ProductCategories/ProductCategory';

// ============================================================================
// EL BONO DE BEBIDAS SE LLEVA EN UN CONTADOR, NO CONTANDO ÓRDENES
//
// El widget del sidebar (`DrinksSold`) leía **todas las órdenes completadas del
// MES en curso** de la sucursal para pintar un número. Y vive dentro del cajón
// de navegación, que es la ÚNICA forma de moverse por el panel y que MUI
// desmonta al cerrarse: cada vez que alguien abría el menú, volvía a leer el mes
// entero. El día 8 del mes eso son ~250-560 documentos por apertura en las
// sucursales grandes; el día 30, más de 2,000.
//
// Ahora hay un contador por sucursal y mes en `counters` —la misma colección de
// los folios y de la versión del catálogo— que se incrementa al completar cada
// pedido. El widget lee **1 documento**.
//
// Idea de Irving (2026-09-09). Es la regla 1 del CLAUDE.md raíz aplicada:
// **contar nunca se hace trayendo documentos.**
//
// ⚠️ La regla de qué cuenta como bebida vive AQUÍ y en un solo lugar. Si el
// contador contara distinto que el widget, el bono del colaborador cambiaría
// según quién lo mire.
// ============================================================================

export const DRINKS_COUNTER_SCOPE = 'drinks';

/** Categoría que define el bono. Es el nombre que el negocio usa en el catálogo. */
export const DRINKS_CATEGORY_NAME = 'Bebidas';

/** Bebidas que hay que vender para ganar cada tramo de bono, y lo que paga. */
export const DRINKS_BONUS_STEP = 100;

/**
 * Primer instante del mes de `date` y del mes siguiente. **La ventana es
 * `[primero, siguiente)`**, así que el último día del mes entra completo.
 *
 * El código anterior usaba `new Date(y, m+1, 0)` —el último día a las 00:00— con
 * una consulta `date < endDate`: **el día 30 o 31 quedaba fuera entero**. No
 * mordió porque la siembra se corrió un día 9, pero el script se anuncia como
 * reejecutable y correrlo el último día del mes habría borrado un día de bono.
 */
export const getMonthWindow = (date: Date): { from: Date; to: Date } => ({
    from: new Date(date.getFullYear(), date.getMonth(), 1),
    to: new Date(date.getFullYear(), date.getMonth() + 1, 1)
});

/** `yyyy-MM` del mes al que pertenece el bono. */
export const getMonthKey = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

/** `drinks_{sucursal}_{yyyy-MM}`: un contador por sucursal y por mes. */
export const getDrinksCounterId = (branchOfficeId: string, date: Date): string =>
    `${DRINKS_COUNTER_SCOPE}_${branchOfficeId}_${getMonthKey(date)}`;

/** Los ids de la categoría "Bebidas" vigentes en el catálogo. */
export const getDrinkCategoryIds = (categories: ProductCategory[]): Set<string> =>
    new Set(
        (categories || [])
            .filter((category) => category?.name === DRINKS_CATEGORY_NAME)
            .map((category) => category.id as string)
    );

/**
 * Cuántas bebidas lleva una orden. **Función pura y única fuente de la regla.**
 *
 * Resuelve por `productCategoryId` contra el catálogo vigente (es lo único que
 * traen las líneas v2) y cae al snapshot de categoría embebido de las líneas v1
 * históricas — sin ese fallback, las órdenes viejas dejarían de sumar al bono.
 */
export const countOrderDrinks = (order: Order, drinkCategoryIds: Set<string>): number =>
    (order?.products || []).reduce((total, line) => {
        if (!line?.product) return total;
        const esBebida =
            drinkCategoryIds.has(line.product.productCategoryId as string) ||
            line.product.productCategoryData?.name === DRINKS_CATEGORY_NAME;
        return esBebida ? total + (Number(line.quantity) || 0) : total;
    }, 0);

/**
 * El bono acumulado: $100 por cada 100 bebidas.
 *
 * (El código anterior hacía `Math.floor(n/100)*100` y luego comprobaba
 * `value % 100 === 0` con un `return 0` en el else — inalcanzable, porque un
 * múltiplo de 100 siempre cumple esa condición. Aquí es la misma cuenta sin la
 * rama muerta.)
 */
export const getDrinksBonus = (drinksCount: number): number =>
    Math.floor(Math.max(0, Number(drinksCount) || 0) / DRINKS_BONUS_STEP) * DRINKS_BONUS_STEP;

export interface DrinksCounter {
    count: number;
    branchOfficeId: string;
    period: string;
    /** Cuándo se calculó desde las órdenes. Sin esto, el contador no es confiable. */
    seededAt?: unknown;
    lastUpdate?: unknown;
}

const counterDoc = (branchOfficeId: string, date: Date) => doc(countersRef, getDrinksCounterId(branchOfficeId, date));

/** El contador del mes: **1 lectura**. `undefined` si nunca se ha sembrado. */
export const readDrinksCounter = async (
    branchOfficeId: string,
    date = new Date()
): Promise<DrinksCounter | undefined> => {
    const snapshot = await getDoc(counterDoc(branchOfficeId, date));
    if (!snapshot.exists()) return undefined;
    const data = snapshot.data() as DrinksCounter;
    // Sin `seededAt` el contador solo tiene los incrementos posteriores al
    // despliegue: mostrarlo sería enseñarle al colaborador un bono más bajo del
    // que ganó. Se trata como si no existiera y quien lo lea lo siembra.
    return data?.seededAt ? data : undefined;
};

/**
 * Fija el contador desde el conteo real de las órdenes del mes. Es la operación
 * de siembra: cuesta lo que cuesta leer el mes, y se paga UNA vez por sucursal.
 */
export const seedDrinksCounter = async (branchOfficeId: string, count: number, date = new Date()): Promise<void> => {
    await setDoc(
        counterDoc(branchOfficeId, date),
        {
            count,
            branchOfficeId,
            period: getMonthKey(date),
            scope: DRINKS_COUNTER_SCOPE,
            seededAt: new Date(),
            lastUpdate: new Date()
        },
        { merge: true }
    );
};

/**
 * Suma las bebidas de un pedido al contador de su sucursal y mes. **Nunca
 * lanza**: el pedido ya se completó y un fallo del contador del bono no puede
 * tirar el cierre de una venta (mismo criterio que el commit de inventario y el
 * pase de Wallet).
 *
 * ⚠️ **`date` DEBE ser la fecha de la ORDEN, no la de hoy.** El recuento (la
 * siembra y cualquier auditoría) filtra por `order.date`, así que si el
 * incremento usara "hoy" las dos fuentes del mismo dinero discreparían: un
 * pedido creado el 30 a las 23:50 y completado el 1 a las 00:10 sumaría a
 * octubre y no aparecería en NINGÚN recuento — el de octubre lo excluye por
 * fecha y septiembre ya estaba sembrado. Con la fecha de la orden, re-sembrar
 * siempre da el mismo número.
 */
export const bumpDrinksCounter = async (branchOfficeId: string, drinks: number, date: Date): Promise<void> => {
    if (!branchOfficeId || drinks <= 0) return;
    try {
        const ref = counterDoc(branchOfficeId, date);
        // ⚠️ Cuesta 1 lectura, y hace falta: **el contador que NACE aquí tiene que
        // nacer sembrado** (2026-09-09).
        //
        // Un mes nuevo empieza en cero por definición, así que el primer pedido
        // del mes ya lo deja correcto. Sin esta marca, el documento nacía sin
        // `seededAt`, el widget lo trataba como "no confiable" y al abrir el menú
        // recontaba el mes y lo PISABA con `setDoc` — perdiendo los incrementos
        // que ya llevaba. Es decir: el bono se habría reiniciado y recontado el
        // día 1 de cada mes, y con dos tablets a la vez, dos veces.
        const existe = (await getDoc(ref)).exists();
        await setDoc(
            ref,
            {
                count: increment(drinks),
                branchOfficeId,
                scope: DRINKS_COUNTER_SCOPE,
                lastUpdate: new Date(),
                ...(existe ? {} : { seededAt: new Date(), period: getMonthKey(date) })
            },
            { merge: true }
        );
    } catch (error) {
        console.error('No se pudo sumar al contador de bebidas', error);
    }
};

/**
 * Descuenta del contador las bebidas de un pedido que se cancela o se borra
 * DESPUÉS de haberse completado. **Nunca lanza** y **nunca crea el documento**:
 * si el contador no existe no hay nada que deshacer, y un contador que naciera
 * en negativo le mostraría al colaborador un bono falso.
 *
 * ⚠️ La fecha es la de la ORDEN, igual que en el incremento: hay que descontar
 * del mes al que se sumó, no del mes en que alguien cancela.
 *
 * Quien llama es responsable de la idempotencia — solo descuenta si el pedido
 * ESTABA completado, y el mismo paso lo deja en otro estado o lo borra.
 */
export const unbumpDrinksCounter = async (branchOfficeId: string, drinks: number, date: Date): Promise<void> => {
    if (!branchOfficeId || drinks <= 0) return;
    try {
        const ref = counterDoc(branchOfficeId, date);
        if (!(await getDoc(ref)).exists()) return;
        await setDoc(ref, { count: increment(-drinks), lastUpdate: new Date() }, { merge: true });
    } catch (error) {
        console.error('No se pudo descontar del contador de bebidas', error);
    }
};
